/**
 * Sprint 22 — ChatPanel.
 *
 * The cockpit chat, sized for the 380px side rail. Layout (top → bottom):
 *  - header: collapse · scope pill · history toggle · new chat
 *  - body: the thread, OR (when history is toggled) a full-width session list
 *  - footer: per-session token/tool-call totals
 *
 * Owns the conversation lifecycle: per-scope active session restored on
 * scope-change / reload (localStorage), new-chat (button + Cmd-K nonce), draft
 * prefill (Cmd-K "Ask about this passage"). Session save is the worker's job
 * (saveConversationServer on stream finish); the panel persists the active
 * session id + refreshes the history list.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../lib/hooks/use-auth";
import { showToast } from "../primitives";
import { useChatRail, type ChatScope } from "./ChatRailContext";
import { useV3ChatRuntime } from "../../hooks/use-v3-chat-runtime";
import { useToolsChanged } from "../../hooks/use-tools-changed";
import { AgentThread } from "./AgentThread";
import { ScopeIndicator } from "./ScopeIndicator";
import { SessionHistoryList } from "./SessionHistorySidebar";
import { CostFooter } from "./CostFooter";
import { chatScopeKey, loadChatSession } from "../../hooks/use-chat-sessions";

function agentNodeIdForScope(scope: ChatScope): string | undefined {
  // Agent + table nodes are valid chat nodes (worker builds an agent/table prompt).
  // Workspace/entity/page fall back to /ai/chat (root) and rely on page_context.
  if ((scope.kind === "agent" || scope.kind === "table") && scope.id) return scope.id;
  return undefined;
}

function activeKey(ws: string | null | undefined, scopeKey: string): string | null {
  return ws ? `kuvio_chat_active_${ws}_${scopeKey}` : null;
}
function readActive(ws: string | null | undefined, scopeKey: string): string | null {
  const k = activeKey(ws, scopeKey);
  if (!k) return null;
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function writeActive(ws: string | null | undefined, scopeKey: string, id: string): void {
  const k = activeKey(ws, scopeKey);
  if (!k) return;
  try {
    localStorage.setItem(k, id);
  } catch {
    /* ignore */
  }
}

// Sprint 23.9 (Fix 1 / Q6) — the session-history open/closed preference. Defaults
// to OPEN so history is discoverable (a collapsed sidebar is an invisible
// feature); persisted across reloads. Not per-workspace — it's a UI habit.
const HISTORY_OPEN_KEY = "kuvio_chat_history_open";
function readHistoryOpen(): boolean {
  try {
    const stored = localStorage.getItem(HISTORY_OPEN_KEY);
    return stored === null ? true : stored === "true";
  } catch {
    return true;
  }
}
function writeHistoryOpen(open: boolean): void {
  try {
    localStorage.setItem(HISTORY_OPEN_KEY, String(open));
  } catch {
    /* ignore */
  }
}

/**
 * №45 — `onCollapse` is GONE, not made optional.
 *
 * The panel used to own one of the two collapse controls: its header's first
 * child was a `ti-chevron-right` button, while the collapsed rail rendered a
 * different button in a different place. One control now lives on the rail's
 * seam (`ChatRail`'s `seamToggle`), positioned identically in both states.
 *
 * Deleted rather than left as an optional prop, because an optional prop is a
 * second control waiting for someone to pass it — and the whole defect was two
 * controls for one action. The header keeps a fixed-width spacer where the
 * button was (`seamSpacer`), so the seam toggle floats over reserved space and
 * `ScopeIndicator` does not shift by a pixel.
 */
export function ChatPanel() {
  const { scope, pageContext, draft, setDraft, newChatNonce } = useChatRail();
  const { currentWorkspaceId } = useAuth();
  const queryClient = useQueryClient();
  const scopeKey = chatScopeKey(scope);

  // Sprint 24.12 — refresh the tool-catalogue caches when the available-tool
  // surface changes out-of-band (an OAuth connector authorize completing). A
  // single mount here is enough; the listener is a window event, scope-agnostic.
  useToolsChanged();

  const [conversationId, setConversationId] = useState<string>(() => crypto.randomUUID());
  // Sprint 23.9 (Fix 1 / Q6 / S23.9.14) — open by default, restored from prefs.
  const [historyOpen, setHistoryOpen] = useState<boolean>(readHistoryOpen);

  const { runtime, chatMessages, setChatMessages, status, error, clearError, regenerate } =
    useV3ChatRuntime({
      conversationId,
      workspaceId: currentWorkspaceId ?? undefined,
      agentNodeId: agentNodeIdForScope(scope),
      scope,
      pageContext,
    });

  // Sweep 2026-08-17 (№40's eslint debt) — this was written DURING RENDER, which
  // `react-hooks/refs` refuses: under concurrent rendering a render can be thrown
  // away, so a ref mutated there can hold a value from a commit that never
  // happened. Behaviour is unchanged by the move: the ref's ONLY consumer is the
  // draft effect below, effects run after commit, and effects in one component
  // fire in declaration order — so this one has always written before that one
  // read. №45 found the error and correctly declined to fix it inside a toggle.
  const runtimeRef = useRef(runtime);
  useEffect(() => {
    runtimeRef.current = runtime;
  }, [runtime]);

  const startNewChat = useCallback(() => {
    setChatMessages([]);
    const id = crypto.randomUUID();
    setConversationId(id);
    writeActive(currentWorkspaceId, scopeKey, id);
    setHistoryOpen(false);
  }, [setChatMessages, currentWorkspaceId, scopeKey]);

  // Restore the active session for this scope on scope-change / first mount.
  const loadedRef = useRef<string>("__init__");
  useEffect(() => {
    if (loadedRef.current === scopeKey) return;
    loadedRef.current = scopeKey;
    // Sprint 23.9 (Fix 2 / Q3 / Q8 / S23.9.13) — a scope change is a HARD reset.
    // useChat owns its `error`/`status:"error"` and doesn't drop it when the
    // transport re-keys, so without this the previous scope's error card bleeds
    // into the new (untouched) scope. clearError() is the canonical reset; the
    // branches below already clear messages + regenerate the conversation id.
    clearError();
    let cancelled = false;
    const storedId = readActive(currentWorkspaceId, scopeKey);
    (async () => {
      if (storedId) {
        try {
          const msgs = await loadChatSession(storedId);
          if (!cancelled && msgs.length > 0) {
            setChatMessages(msgs);
            setConversationId(storedId);
            return;
          }
        } catch {
          /* fall through to a fresh session */
        }
      }
      if (!cancelled) {
        setChatMessages([]);
        setConversationId(crypto.randomUUID());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scopeKey, currentWorkspaceId, setChatMessages, clearError]);

  // Sprint 23.9 (Q6 / S23.9.14) — persist ONLY an explicit toggle, not the
  // transient view flips from startNewChat / onSelectSession (which set
  // historyOpen=false to show the thread). Conflating the two would let
  // "resume a chat" silently overwrite the user's "keep history open" choice.
  const toggleHistory = useCallback(() => {
    setHistoryOpen((v) => {
      const next = !v;
      writeHistoryOpen(next);
      return next;
    });
  }, []);

  // Cmd-K "New chat" nonce.
  const nonceRef = useRef(newChatNonce);
  useEffect(() => {
    if (nonceRef.current === newChatNonce) return;
    nonceRef.current = newChatNonce;
    startNewChat();
  }, [newChatNonce, startNewChat]);

  // Draft (Cmd-K "Ask about this passage") — append + send, then clear.
  useEffect(() => {
    if (draft == null) return;
    try {
      runtimeRef.current.thread.append({ role: "user", content: [{ type: "text", text: draft }] });
    } catch {
      /* ignore */
    }
    setDraft(null);
  }, [draft, setDraft]);

  // On stream finish: remember this session for the scope + refresh history.
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const was = prevStatusRef.current;
    prevStatusRef.current = status;
    if ((was === "streaming" || was === "submitted") && status === "ready") {
      writeActive(currentWorkspaceId, scopeKey, conversationId);
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    }
  }, [status, conversationId, currentWorkspaceId, scopeKey, queryClient]);

  const onSelectSession = useCallback(
    async (id: string) => {
      try {
        const msgs = await loadChatSession(id);
        setChatMessages(msgs);
        setConversationId(id);
        writeActive(currentWorkspaceId, scopeKey, id);
        setHistoryOpen(false);
      } catch {
        showToast("Couldn't load that chat", "error");
      }
    },
    [setChatMessages, currentWorkspaceId, scopeKey],
  );

  // Sprint 23.9 (Fix 3 / S23.9.10) — inline retry. clearError() dismisses the
  // card immediately; regenerate() re-runs the last turn against the worker
  // (which streams from body.messages, so no duplicate user message is created).
  const onRetry = useCallback(() => {
    clearError();
    regenerate();
  }, [clearError, regenerate]);

  const scopeName = scope.label ?? (scope.kind === "workspace" ? "this workspace" : `this ${scope.kind}`);

  return (
    <div style={root}>
      <header style={headerRow}>
        {/* №45 — reserved space for the rail's seam toggle, which is absolutely
            positioned over exactly these coordinates. Keeping the width means
            the header's layout is byte-identical to before the toggle moved. */}
        <div style={seamSpacer} aria-hidden="true" />
        <div style={scopeWrap}>
          <ScopeIndicator />
        </div>
        <button
          type="button"
          onClick={toggleHistory}
          style={{ ...iconBtn, ...(historyOpen ? iconBtnActive : null) }}
          title="Chat history"
          aria-label="Chat history"
        >
          <i className="ti ti-history" style={{ fontSize: 16 }} aria-hidden="true" />
        </button>
        <button type="button" onClick={startNewChat} style={iconBtn} title="New chat (⌘N)" aria-label="New chat">
          <i className="ti ti-plus" style={{ fontSize: 16 }} aria-hidden="true" />
        </button>
      </header>

      <div style={body}>
        {historyOpen ? (
          <SessionHistoryList activeSessionId={conversationId} onSelect={onSelectSession} />
        ) : (
          <div style={threadArea}>
            <AgentThread
              runtime={runtime}
              chatMessages={chatMessages}
              chatStatus={status}
              chatError={error}
              onRetry={onRetry}
              onOAuthContinue={regenerate}
              assistantLabel="Kuvio"
              emptyState={
                <div style={empty}>
                  <i className="ti ti-message-circle" style={{ fontSize: 26, opacity: 0.4 }} aria-hidden="true" />
                  <p style={emptyTitle}>Ask about {scopeName}</p>
                  <p style={emptySub}>
                    Operate your workspace in natural language — query tasks, tables, agents, retros, and more.
                  </p>
                </div>
              }
            />
          </div>
        )}
      </div>

      {!historyOpen && <CostFooter messages={chatMessages} />}
    </div>
  );
}

const root: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
  overflow: "hidden",
  fontFamily: "var(--font-sans)",
};

const headerRow: CSSProperties = {
  flex: "0 0 auto",
  height: 44,
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "0 6px 0 6px",
  borderBottom: "var(--kuvio-border-default)",
};

const scopeWrap: CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
};

/** №45 — the width the seam toggle floats over. Same 30px as `iconBtn`. */
const seamSpacer: CSSProperties = {
  flex: "0 0 auto",
  width: 30,
  height: 30,
};

const iconBtn: CSSProperties = {
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  borderRadius: "var(--border-radius-sm)",
};

const iconBtnActive: CSSProperties = {
  background: "var(--color-background-secondary)",
  color: "var(--color-text-primary)",
};

const body: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  display: "flex",
  overflow: "hidden",
};

const threadArea: CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  minHeight: 0,
  overflow: "hidden",
  display: "flex",
};

const empty: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  padding: "28px 20px",
  textAlign: "center",
};

const emptyTitle: CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: "var(--color-text-primary)",
  margin: 0,
};

const emptySub: CSSProperties = {
  fontSize: 12.5,
  lineHeight: 1.5,
  color: "var(--color-text-tertiary)",
  margin: 0,
  maxWidth: 280,
};
